/**
 * The data seam — web mirror of the Swift app's `Sources/Store/FactoryStore.swift`.
 *
 * RLS scopes every read to the caller's tenant, and apps may only INSERT
 * `factory_commands` — so this interface is READ-everything + submit-a-command,
 * nothing else. Mock today; Supabase later, no view change.
 */

import type {
  FactoryApprovalRow,
  FactoryCommandInsert,
  FactoryCommandRow,
  FactoryEventRow,
  FactoryHealthRow,
  FactoryPrTaskRow,
  FactoryTaskRow,
} from '../models/factory';

/** A consistent snapshot of the factory read-model for a tenant. */
export interface FactorySnapshot {
  tasks: FactoryTaskRow[];
  prTasks: FactoryPrTaskRow[];
  approvals: FactoryApprovalRow[];
  events: FactoryEventRow[];
  health: FactoryHealthRow | null;
}

export function emptySnapshot(): FactorySnapshot {
  return { tasks: [], prTasks: [], approvals: [], events: [], health: null };
}

export interface LiveUpdateOptions {
  /** Poll cadence for the fallback loop (and the default poller), in ms. */
  intervalMs?: number;
}

export interface FactoryStore {
  fetchTasks(): Promise<FactoryTaskRow[]>;
  fetchPRTasks(): Promise<FactoryPrTaskRow[]>;
  fetchApprovals(): Promise<FactoryApprovalRow[]>;
  fetchEvents(limit: number): Promise<FactoryEventRow[]>;
  fetchHealth(): Promise<FactoryHealthRow | null>;

  /** The ONLY write path. Enqueues a command (status defaults to `queued`
   * server-side); the engine drains it. */
  submit(command: FactoryCommandInsert): Promise<FactoryCommandRow>;

  /** Convenience aggregate used by the dashboard. */
  fetchSnapshot(): Promise<FactorySnapshot>;

  /**
   * OPTIONAL live-updates channel. When present it OWNS the update loop and
   * returns an unsubscribe fn. `SupabaseFactoryStore` implements it with Supabase
   * Realtime (primary) + a polling fallback; stores that omit it (MockFactoryStore)
   * fall back to `pollSnapshots` in `AppEnvironment` — so Mock stays pure polling
   * and unchanged.
   */
  liveUpdates?(onSnapshot: (snap: FactorySnapshot) => void, options?: LiveUpdateOptions): () => void;
}

/** Default aggregate: fetch every table in parallel into one snapshot. */
export async function fetchSnapshotDefault(store: FactoryStore): Promise<FactorySnapshot> {
  const [tasks, prTasks, approvals, events, health] = await Promise.all([
    store.fetchTasks(),
    store.fetchPRTasks(),
    store.fetchApprovals(),
    store.fetchEvents(50),
    store.fetchHealth(),
  ]);
  return { tasks, prTasks, approvals, events, health };
}

/**
 * Live updates via POLLING — the realtime fallback used while the factory_*
 * tables are NOT in the `supabase_realtime` publication (see
 * SupabaseFactoryStore's realtime TODO). Invokes `onSnapshot` every
 * `intervalMs` until the returned unsubscribe fn is called. Works for BOTH Mock
 * and Supabase; when realtime is enabled, a store can swap this for a channel.
 */
export function pollSnapshots(
  store: FactoryStore,
  onSnapshot: (snap: FactorySnapshot) => void,
  intervalMs = 5000,
): () => void {
  let cancelled = false;
  const timer = setInterval(async () => {
    if (cancelled) return;
    try {
      const snap = await store.fetchSnapshot();
      if (!cancelled) onSnapshot(snap);
    } catch {
      /* transient fetch failure — keep polling */
    }
  }, intervalMs);

  return () => {
    cancelled = true;
    clearInterval(timer);
  };
}

export class FactoryStoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FactoryStoreError';
  }
}
